/**
 * Sign in with Apple and Teslyn's invitations (decision 923; docs/reference/setup-push-apple-passkeys.md §3.12; R155).
 *
 * Teslyn lets in only people who were invited, by their email address. The first time someone uses Sign in with Apple,
 * Apple asks "Share My Email" or "Hide My Email". Hide My Email gives Teslyn a made-up relay address
 * (`…@privaterelay.appleid.com`) that matches no invitation, so Supabase refuses the sign-in as a sign-up, and the
 * person must be told why and how to undo the choice (Apple asks only once). These rules give the words for the phone
 * (which knows the address Apple gave, from the identity token) and for Teslyn Cloud's web page (which only sees the
 * refusal come back).
 */

/** Apple's relay domain for Hide My Email addresses. */
export const APPLE_RELAY_DOMAIN = 'privaterelay.appleid.com';

/** Whether an address is one of Apple's Hide My Email relay addresses. */
export function isAppleRelayEmail(email: string | null | undefined): boolean {
  const e = (email ?? '').trim().toLowerCase();
  return e.endsWith(`@${APPLE_RELAY_DOMAIN}`);
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** base64url (or base64) to text, UTF-8, with no Buffer, atob or TextDecoder (Hermes on the phone lacks some). */
function base64urlText(s: string): string | null {
  const clean = s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of clean) {
    const v = B64URL.indexOf(ch);
    if (v < 0) return null;
    value = (value << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((value >> bits) & 0xff);
    }
  }
  try {
    return decodeURIComponent(bytes.map((b) => `%${b.toString(16).padStart(2, '0')}`).join(''));
  } catch {
    return null;
  }
}

/**
 * The address in an Apple identity token (a JWT; only its claims are read, never trusted for anything but the words:
 * Supabase checks the token itself) and whether Apple says it is a Hide My Email address (`is_private_email`, or the
 * relay domain). Null address when the token cannot be read or has none.
 */
export function appleTokenEmail(idToken: string | null | undefined): {
  readonly email: string | null;
  readonly hidden: boolean;
} {
  const part = (idToken ?? '').split('.')[1];
  const text = part ? base64urlText(part) : null;
  let claims: { email?: unknown; is_private_email?: unknown } = {};
  try {
    claims = text ? (JSON.parse(text) as typeof claims) : {};
  } catch {
    claims = {};
  }
  const email = typeof claims.email === 'string' && claims.email.trim() ? claims.email.trim() : null;
  const flagged = claims.is_private_email === true || claims.is_private_email === 'true';
  return { email, hidden: flagged || isAppleRelayEmail(email) };
}

/** Supabase's answer when the address has no invitation (sign-ups are off: invite only). */
export function refusedAsNotInvited(message: string | null | undefined): boolean {
  return /signup|sign-up|sign up|not allowed|not invited|user not found/i.test(message ?? '');
}

/** Where someone undoes Hide My Email, on an iPhone and on a computer (Apple asks again at the next sign-in). */
export const APPLE_STOP_USING_STEPS =
  'On your iPhone: Settings, your name, Sign-In & Security, Sign in with Apple, Teslyn Mobile, Stop Using. On a computer: ' +
  'account.apple.com, Sign-In and Security, Sign in with Apple.';

/** Hide My Email was chosen: the relay address matches no invitation. */
export const APPLE_HIDE_MY_EMAIL_WORDS =
  'Teslyn could not let you in: you chose Hide My Email, so Apple gave Teslyn a made-up address that matches no ' +
  'invitation (Teslyn is by invitation only). To use Sign in with Apple, stop using it for Teslyn Mobile (' +
  APPLE_STOP_USING_STEPS +
  '), then sign in with Apple again and choose Share My Email. Or sign in another way.';

/** The address Apple shared has no invitation. */
export function appleNotInvitedWords(email: string): string {
  return (
    `The email Apple shared, ${email}, has not been invited to Teslyn. Ask your administrator to invite that ` +
    'address, or sign in another way with the address your invitation went to.'
  );
}

/** Teslyn Cloud's page: the refusal came back without the address, so both causes are named. */
export const APPLE_REFUSED_WEB_WORDS =
  'This Apple account has not been invited. Teslyn is by invitation only and matches the email Apple shares. If ' +
  'you chose Hide My Email, Apple gave Teslyn a made-up address: stop using Sign in with Apple for Teslyn Mobile (' +
  APPLE_STOP_USING_STEPS +
  '), then sign in with Apple again and choose Share My Email. Otherwise ask your administrator to invite the ' +
  'email your Apple account uses.';

/**
 * The words for a Sign in with Apple that Teslyn refused as not invited, or null when the refusal is something else
 * (the caller shows its own words). `email` and `hidden` come from the identity token on the phone; on the web they
 * are unknown.
 */
export function appleRefusalWords(input: {
  readonly message: string | null | undefined;
  readonly email?: string | null;
  readonly hidden?: boolean;
}): string | null {
  if (!refusedAsNotInvited(input.message)) return null;
  if (input.hidden || isAppleRelayEmail(input.email)) return APPLE_HIDE_MY_EMAIL_WORDS;
  if (input.email) return appleNotInvitedWords(input.email);
  return APPLE_REFUSED_WEB_WORDS;
}
